import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "@/services/api";
import type { Page, Source } from "@/types";

/**
 * React Query hooks for the admin Sources feature. These wrap the existing API
 * client methods — the client is unchanged — and own the query keys, caching,
 * and invalidation-on-success that previously lived as manual `useEffect`
 * loaders in the page. This module is the template later admin slices copy.
 */

export interface SourcesListParams {
  page: number;
  page_size: number;
  active_only: boolean;
  q?: string;
  sort_by?: string;
  sort_order?: "asc" | "desc";
}

export const sourceKeys = {
  all: ["admin", "sources"] as const,
  lists: () => [...sourceKeys.all, "list"] as const,
  list: (params: SourcesListParams) => [...sourceKeys.lists(), params] as const,
};

export function useSources(params: SourcesListParams) {
  return useQuery<Page<Source>>({
    queryKey: sourceKeys.list(params),
    queryFn: () => api.getSources(params),
  });
}

/** Invalidate every Sources query (all list variants) after a write. */
function useInvalidateSources() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: sourceKeys.all });
}

export function useCreateSource() {
  const invalidate = useInvalidateSources();
  return useMutation({
    mutationFn: (data: Partial<Source>) => api.createSource(data),
    onSuccess: invalidate,
  });
}

export function useUpdateSource() {
  const invalidate = useInvalidateSources();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Source> }) => api.updateSource(id, data),
    onSuccess: invalidate,
  });
}

export function useDeleteSource() {
  const invalidate = useInvalidateSources();
  return useMutation({
    mutationFn: (id: string) => api.deleteSource(id),
    onSuccess: invalidate,
  });
}
